import { atom } from "jotai";
import { atomFamily, loadable } from "jotai/utils";

import { zslAtom } from "../templates";
import { Zap, ZapMetadata } from "../types";
import { extractAssetId, isTemplateZapId, isZapErrorObject } from "../utils";
import { currentAccountIdAtom } from "../account";
import { canvasIdAtom } from "../canvas";

export type ZapErrorObject = {
  error: string;
  status: number;
};

type ZapAtomReturnType = Zap | ZapErrorObject | null;
type ZapMetadataAtomReturnType = ZapMetadata | ZapErrorObject | null;

async function getZapMetadata(zapId: string, canvasId: string) {
  try {
    const res = await fetch(
      `/api/gulliver/metadata/v1/zaps/${zapId}?container=canvas:${canvasId}`,
    );
    if (!res.ok) {
      return {
        error: "Zap Metadata not found",
        status: res.status,
      };
    }
    return await res.json();
  } catch (error: unknown) {
    const errorMessage =
      error instanceof Error ? error.message : `Error fetching Zap id ${zapId}`;

    return {
      error: errorMessage,
      status: 500, // Network or other error
    };
  }
}

async function getZap(zapId: string, account_id: number) {
  try {
    const res = await fetch(
      `/api/gulliver/storage/v1/zaps/${zapId}?account_id=${account_id}`,
    );
    if (!res.ok) {
      throw new Error("Zap not found");
    }
    return await res.json();
  } catch (error: unknown) {
    if (error instanceof Error) {
      return { error: error.message };
    }
    return { error: `Error fetching Zap id ${zapId}` };
  }
}

export const zapAtomFamily_SUSPENDS = atomFamily(
  (zapId: string | undefined) => {
    const zapAsyncAtom = atom<Promise<ZapAtomReturnType>>(async (get) => {
      const accountId = get(currentAccountIdAtom);
      if (!zapId || !accountId) {
        return null;
      }

      const isTemplateZap = isTemplateZapId(zapId);
      const zsl = get(zslAtom);

      if (zsl && isTemplateZap) {
        const coreZapId = extractAssetId(zapId); // For templates, zapId might be ${{zaps._ZAP_1}} so we want to extract ZAP_1 from it

        const zapTemplate = zsl?.zaps?.find((zap) => coreZapId === zap.id);

        if (zapTemplate) {
          return {
            ...zapTemplate,
            current_version: {
              zdl: zapTemplate.zdl,
            },
          };
        }
      }

      // Because we don't want to fetch with template asset id and get a 404 Not Found error
      if (isTemplateZap) {
        return null;
      }

      return await getZap(zapId, accountId);
    });

    zapAsyncAtom.debugLabel = `zapAtomFamily_SUSPENDS(${zapId})`;
    return zapAsyncAtom;
  },
);

export const zapMetadataAtomFamily_SUSPENDS = atomFamily(
  (zapId: string | undefined) => {
    const zapAsyncAtom = atom<Promise<ZapMetadataAtomReturnType>>(
      async (get) => {
        if (!zapId) {
          return null;
        }

        const isTemplateZap = isTemplateZapId(zapId);
        const zsl = get(zslAtom);

        if (zsl && isTemplateZap) {
          const coreZapId = extractAssetId(zapId); // For templates, zapId might be ${{zaps._ZAP_1}} so we want to extract ZAP_1 from it

          const zapTemplate = zsl?.zaps?.find((zap) => coreZapId === zap.id);

          if (zapTemplate) {
            return {
              ...zapTemplate,
              zap_id: zapTemplate.id,
              steps: zapTemplate.zdl?.steps,
            };
          }
        }

        // Because we don't want to fetch with template asset id and get a 404 Not Found error
        if (isTemplateZap) {
          return null;
        }

        const canvasId = get(canvasIdAtom);
        return await getZapMetadata(zapId, canvasId);
      },
    );

    zapAsyncAtom.debugLabel = `zapMetadataAtomFamily_SUSPENDS(${zapId})`;
    return zapAsyncAtom;
  },
);

// Does NOT suspend due to loadable usage
export const loadableZapMetadataAsyncAtomFamily = atomFamily(
  (zapId: string | undefined) => {
    const loadableZapAsyncAtom = loadable(
      zapMetadataAtomFamily_SUSPENDS(zapId),
    );
    loadableZapAsyncAtom.debugLabel = `loadableZapMetadataAsyncAtomFamily(${zapId})`;
    return loadableZapAsyncAtom;
  },
);

export const zapNoteAtomFamily_SUSPENDS = atomFamily(
  (zapId: string | undefined) => {
    const zapNoteAtom = atom(async (get) => {
      const zap = await get(zapAtomFamily_SUSPENDS(zapId));
      if (isZapErrorObject(zap)) {
        return null;
      }
      return zap?.current_version?.zap_note || zap?.draft?.zap_note || null;
    });

    zapNoteAtom.debugLabel = `zapNoteAtomFamily_SUSPENDS(${zapId})`;
    return zapNoteAtom;
  },
);
